/**
 * Friction surveys and a site's model figures, read for the friction and
 * rubber boards.
 *
 * Both are read only once the board's engine is here (`enabled`), so a page
 * with no board never asks for them. The keys are namespaced by site, like
 * every other register, and are what a write invalidates once the platform
 * has taken it.
 */
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../app/SessionContext";

/** The model the rubber planner's figures are saved for. */
export const RUBBER_MODEL = "contaminant_accumulation";

export const frictionKeys = {
  surveys: (siteId: string) => ["friction-surveys", siteId] as const,
  modelParams: (siteId: string, model: string) => ["model-params", siteId, model] as const,
  testRecords: (siteId: string) => ["test-records", siteId] as const,
  workOrders: (siteId: string) => ["work-orders", siteId] as const,
};

export function useFrictionSurveys(opts: { enabled: boolean }) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: frictionKeys.surveys(site.id),
    queryFn: () => source.listFrictionSurveys(site.id),
    enabled: opts.enabled,
    staleTime: 30_000,
    /* a 403 is an answer, not a blip: say it rather than retry it */
    retry: (n, e) => !(e as { status?: number })?.status && n < 2,
  });
}

export function useModelParams(model: string, opts: { enabled: boolean }) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: frictionKeys.modelParams(site.id, model),
    queryFn: () => source.getModelParams(site.id, model),
    enabled: opts.enabled,
    staleTime: 60_000,
    retry: (n, e) => !(e as { status?: number })?.status && n < 2,
  });
}

/** The site's test records, shared with the testing screens' cache. */
export function useFrictionTestRecords(opts: { enabled: boolean }) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: frictionKeys.testRecords(site.id),
    queryFn: () => source.listTestRecords(site.id),
    enabled: opts.enabled,
    staleTime: 30_000,
    retry: (n, e) => !(e as { status?: number })?.status && n < 2,
  });
}
